/**
 * Currency formatting utilities for ledger/API amounts.
 *
 * Unit reference:
 * - Cents: 1 unit = 100 cents (transactions.amount_in_cents, ledger_bank_statement_lines.amount_in_cents)
 * - Millicents: 1 unit = 100,000 millicents (ledger_payouts, TigerBeetle, case balances)
 *
 * Locale and symbol are derived from the currency code (EUR → pt-PT "1.234,56 €",
 * GBP → en-GB "£1,234.56"). `currency` is required — pass `useCurrency().currency` (operator default) or the row's own code.
 */

import { currencySymbol, currencyToLocale, formatMoney, formatMoneyCompact } from '@/lib/currency';

/**
 * Format millicents as currency (canonical for ledger/API amounts)
 *
 * @param millicents - Amount in millicents (1 unit = 100,000 millicents)
 * @param currency - ISO 4217 currency code
 * @returns Formatted currency string (e.g., "1,50 €" or "£1,234.56")
 */
export function formatCurrency(millicents: number, currency: string): string {
  return formatMoney(millicents / 100_000, currency);
}

/**
 * Format currency without symbol (for PDFs or custom formatting)
 *
 * @param millicents - Amount in millicents
 * @param currency - ISO 4217 currency code; governs separators
 * @returns Formatted amount string (e.g., "1,50" or "1,234.56")
 */
export function formatAmount(millicents: number, currency: string): string {
  return new Intl.NumberFormat(currencyToLocale(currency), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(millicents / 100_000);
}

/**
 * Format currency with a leading symbol (for PDFs that need a symbol prefix)
 *
 * @param millicents - Amount in millicents
 * @param currency - ISO 4217 currency code
 * @returns Formatted currency string with symbol prefix (e.g., "€1,50", "£1,234.56")
 */
export function formatCurrencyWithSymbol(millicents: number, currency: string): string {
  return `${currencySymbol(currency)}${formatAmount(millicents, currency)}`;
}

/**
 * Format cents as currency (for transactions.amount_in_cents, bank lines)
 *
 * @param cents - Amount in cents (1 unit = 100 cents)
 * @param currency - ISO 4217 currency code
 * @returns Formatted currency string
 */
export function formatCents(cents: number, currency: string): string {
  return formatCurrency(cents * 1000, currency);
}

/** Typed major-unit amount → integer cents, or null when it is not a positive two-decimal amount. */
export function parseMajorToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  return cents > 0 ? cents : null;
}

export function centsToMajorInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Compact currency for dense chart labels — thousands/millions with a `k`/`M` suffix.
 *
 * @param millicents - Amount in millicents
 * @param currency - ISO 4217 currency code
 * @param decimals - Decimal places for the `k`/`M` form (segment labels use 1, title totals use 0)
 * @returns e.g. "€7.6k", "£10k", "€1.2M", "£500" (sub-1k shown whole)
 */
export function formatCurrencyCompact(millicents: number, currency: string, decimals = 1): string {
  return formatMoneyCompact(millicents / 100_000, currency, decimals);
}
